import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios'
// Offline mode disabled
// import { offlineService } from '../services/offlineService'
import { getApiBaseUrl, isPrivateOrTailscaleHost, isTailscaleBrowserPage } from '../services/apiConfig'
import { secureStorage } from '../utils/secureStorage'
import { generateHardDiskId, generateStableDeviceId } from '../utils/hardwareId'
import { incrementApiActivity, decrementApiActivity } from '../utils/apiActivity'
import { enrichAxiosError, readUiLanguage } from '../utils/apiErrorMessage'

// Get base URL dynamically (from localStorage or environment)
const getDynamicApiBaseUrl = () => {
  return getApiBaseUrl()
}

function resolveRequestTimeoutMs(): number {
  try {
    const raw = getApiBaseUrl()
    const parsed = new URL(raw.startsWith('http') ? raw : `http://${raw}`)
    const host = parsed.hostname.replace(/^\[|\]$/g, '').toLowerCase()
    const isLoopback = host === 'localhost' || host === '127.0.0.1' || host === '::1'
    if (!isLoopback && isPrivateOrTailscaleHost(host)) return 18000
  } catch {
    // keep default
  }
  return 30000
}

/**
 * Tailscale HTTP/1.1 has few sockets. Cap concurrency so heartbeats cannot
 * starve the UI — but never wait forever (that shows as (pending) in DevTools).
 */
const TAILSCALE_MAX_CONCURRENT = 8
const SLOT_WAIT_MS = 1200
const MAX_WAITERS = 12
let tailscaleActive = 0
const tailscalePriorityWaiters: Array<() => void> = []
const tailscaleWaiters: Array<() => void> = []

function isBackgroundRequestUrl(url: string): boolean {
  const u = url.toLowerCase()
  return (
    u.includes('/deviceregistration/presence') ||
    u.includes('/registry/poll') ||
    u.includes('/license/update-location') ||
    u.includes('/license/active') ||
    u.includes('/license/check') ||
    u.includes('/screen-view') ||
    u.includes('/fuel-station/notifications') ||
    u.includes('/notifications/inbox') ||
    u.includes('/hubs/')
  )
}

function acquireTailscaleSlot(priority = false): Promise<boolean> {
  if (typeof window === 'undefined' || !isTailscaleBrowserPage()) return Promise.resolve(false)
  if (tailscaleActive < TAILSCALE_MAX_CONCURRENT) {
    tailscaleActive += 1
    return Promise.resolve(true)
  }

  return new Promise((resolve) => {
    let settled = false
    const finish = (gotSlot: boolean) => {
      if (settled) return
      settled = true
      if (gotSlot) tailscaleActive += 1
      resolve(gotSlot)
    }

    const grant = () => finish(true)
    const queue = priority ? tailscalePriorityWaiters : tailscaleWaiters
    if (queue.length >= MAX_WAITERS) {
      finish(false)
      return
    }
    queue.push(grant)
    window.setTimeout(() => {
      const idx = queue.indexOf(grant)
      if (idx >= 0) queue.splice(idx, 1)
      finish(false)
    }, SLOT_WAIT_MS)
  })
}

function releaseTailscaleSlot(): void {
  if (typeof window === 'undefined' || !isTailscaleBrowserPage()) return
  tailscaleActive = Math.max(0, tailscaleActive - 1)
  const next = tailscalePriorityWaiters.shift() || tailscaleWaiters.shift()
  if (next) next()
}

function releaseFromConfig(config?: InternalAxiosRequestConfig | null): void {
  const flagged = config as (InternalAxiosRequestConfig & { _tailscaleSlot?: boolean }) | undefined
  if (!flagged?._tailscaleSlot) return
  flagged._tailscaleSlot = false
  releaseTailscaleSlot()
}

// Create axios instance with dynamic base URL
const apiClient: AxiosInstance = axios.create({
  baseURL: getDynamicApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: resolveRequestTimeoutMs(),
})

let cachedHardwareId: string | undefined
let cachedDeviceId: string | undefined

function getCachedDeviceHeaders(): { hid: string; did: string } {
  if (!cachedHardwareId) cachedHardwareId = generateHardDiskId()
  if (!cachedDeviceId) cachedDeviceId = generateStableDeviceId()
  return { hid: cachedHardwareId, did: cachedDeviceId }
}

// Interceptor to update base URL dynamically on each request
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const url = config.url ?? ''
    if (!isBackgroundRequestUrl(url)) {
      const method = (config.method || 'get').toLowerCase()
      const priority = method !== 'get' && method !== 'head' && method !== 'options'
      const gotSlot = await acquireTailscaleSlot(priority)
      if (gotSlot) {
        ;(config as InternalAxiosRequestConfig & { _tailscaleSlot?: boolean })._tailscaleSlot = true
      }
    }
    // تحديث baseURL في كل طلب (للتأكد من استخدام أحدث قيمة)
    const currentBaseUrl = getApiBaseUrl()
    if (config.baseURL !== currentBaseUrl) {
      config.baseURL = currentBaseUrl
    }
    if (config.timeout == null || config.timeout === 30000) {
      config.timeout = resolveRequestTimeoutMs()
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers as { delete?: (name: string) => void; ['Content-Type']?: string }
      headers.delete?.('Content-Type')
      delete headers['Content-Type']
    }
    return config
  },
  (error) => {
    releaseFromConfig(error?.config)
    return Promise.reject(error)
  }
)

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = secureStorage.getItem('token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    if (config.headers) {
      config.headers['X-Language'] = readUiLanguage()
    }
    try {
      const { hid, did } = getCachedDeviceHeaders()
      if (config.headers) {
        if (hid) config.headers['X-Hardware-Id'] = hid
        if (did) config.headers['X-Device-Id'] = did
      }
    } catch {
      /* ignore fingerprint errors */
    }
    const url = config.url ?? ''
    // Heartbeats must not occupy HTTP/1.1 slots for 90s (Tailscale default).
    if (isBackgroundRequestUrl(url)) {
      config.timeout = 8000
    }
    if (!url.includes('/deviceregistration/presence') && !url.includes('/registry/poll')) {
      incrementApiActivity()
    }
    return config
  },
  (error) => {
    releaseFromConfig(error?.config)
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors and offline mode
apiClient.interceptors.response.use(
  (response) => {
    releaseFromConfig(response.config)
    const url = response.config.url ?? ''
    if (!url.includes('/deviceregistration/presence') && !url.includes('/registry/poll')) {
      decrementApiActivity()
    }
    return response
  },
  async (error: AxiosError) => {
    releaseFromConfig(error.config)
    const url = error.config?.url ?? ''
    if (!url.includes('/deviceregistration/presence') && !url.includes('/registry/poll')) {
      decrementApiActivity()
    }
    if (!error.response) {
      ;(error as AxiosError & { isApiUnavailable?: boolean }).isApiUnavailable = true
    } else {
      enrichAxiosError(error)
    }

    // Offline mode is disabled - skip offline queue handling
    // Handle network errors (offline mode DISABLED)
    // if (!error.response && error.code === 'ERR_NETWORK') {
    //   // Check if this is a POST/PUT/DELETE request that should be queued
    //   const config = error.config as InternalAxiosRequestConfig & { _skipOfflineQueue?: boolean }
    //   
    //   if (config && !config._skipOfflineQueue) {
    //     const method = config.method?.toUpperCase()
    //     
    //     // Queue POST, PUT, DELETE requests for offline sync
    //     if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
    //       const action = method === 'POST' ? 'create' : method === 'PUT' ? 'update' : 'delete'
    //       
    //       // Extract invoice data from request
    //       const invoiceData = config.data ? JSON.parse(config.data) : {}
    //       
    //       // Add to offline queue
    //       const localId = offlineService.addInvoice(invoiceData, action)
    //       
    //       // Return a special error that indicates offline mode
    //       const offlineError = new Error('OFFLINE_MODE') as any
    //       offlineError.isOffline = true
    //       offlineError.localId = localId
    //       offlineError.message = 'تم حفظ الفاتورة محلياً. سيتم مزامنتها عند عودة الاتصال.'
    //       
    //       return Promise.reject(offlineError)
    //     }
    //   }
    // }

    if (error.response?.status === 403) {
      const body = error.response.data as { error?: string } | undefined
      if (body?.error === 'DeviceBlocked') {
        ;(error as AxiosError & { isDeviceBlocked?: boolean }).isDeviceBlocked = true
      }
    }

    // Handle 401 Unauthorized - only redirect if not already on login page
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname
      const requestUrl = error.config?.url || ''

      // Session refresh failure: let SessionExpiryMonitor / caller handle logout (avoid double redirect)
      if (requestUrl.includes('/auth/refresh')) {
        return Promise.reject(error)
      }
      
      // Don't redirect if:
      // 1. Already on login page
      // 2. It's a login request (to avoid redirect loop)
      // 3. It's a public endpoint that doesn't require auth
      if (currentPath !== '/login' &&
          !currentPath.startsWith('/developers') &&
          !currentPath.startsWith('/shop') &&
          !requestUrl.includes('/auth/login') &&
          !requestUrl.includes('/auth/register') &&
          !requestUrl.includes('/developer-portal') &&
          !requestUrl.includes('/commerce/public')) {
        
        // Clear auth data
        secureStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('auth-storage')
        
        // Only redirect if we're in a protected route
        if (!currentPath.startsWith('/login')) {
          // Use window.location.href for full page reload to clear state
          window.location.href = '/login'
          return Promise.reject(error)
        }
      }
    }
    return Promise.reject(error)
  }
)

export { apiClient }

